// AppsFlyer Smart Script Loader and OneLink URL Generator

import { APPSFLYER_CONFIG, APPSFLYER_SMART_SCRIPT_URL } from 'shared/components/AppMigration/constants';
import { IOneLinkResult, ISmartScriptConfig } from 'shared/components/AppMigration/types';
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';

// Track script loading state
let isScriptLoading = false;
let isScriptLoaded = false;
let scriptLoadPromise: Promise<void> | null = null;

/**
 * Load the AppsFlyer Smart Script dynamically
 * @returns Promise that resolves when script is loaded
 */
export const loadAppsFlyerSmartScript = (): Promise<void> => {
  // Return immediately if already loaded
  if (isScriptLoaded && window.AF_SMART_SCRIPT) {
    return Promise.resolve();
  }

  // Reuse existing promise if script is already loading
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  // Safety fallback: if loading is in progress but promise was lost, poll with timeout
  if (isScriptLoading) {
    return new Promise((resolve, reject) => {
      const timeoutMs = 10000;
      const checkLoaded = setInterval(() => {
        if (isScriptLoaded) {
          clearInterval(checkLoaded);
          clearTimeout(timeoutId);
          resolve();
        }
      }, 100);
      const timeoutId = setTimeout(() => {
        clearInterval(checkLoaded);
        reject(new Error('Timed out loading AppsFlyer Smart Script'));
      }, timeoutMs);
    });
  }

  isScriptLoading = true;

  scriptLoadPromise = new Promise((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.querySelector(
      `script[src="${APPSFLYER_SMART_SCRIPT_URL}"]`
    );

    if (existingScript) {
      // Script tag exists, check if AF_SMART_SCRIPT is available
      if (window.AF_SMART_SCRIPT) {
        isScriptLoaded = true;
        isScriptLoading = false;
        resolve();
        return;
      }

      // Wait for existing script to load
      existingScript.addEventListener('load', () => {
        isScriptLoaded = true;
        isScriptLoading = false;
        resolve();
      });

      existingScript.addEventListener('error', () => {
        isScriptLoading = false;
        reject(new Error('Failed to load AppsFlyer Smart Script'));
      });
      return;
    }

    // Create and append new script
    const script = document.createElement('script');
    script.src = APPSFLYER_SMART_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      isScriptLoaded = true;
      isScriptLoading = false;
      resolve();
    };

    script.onerror = () => {
      isScriptLoading = false;
      scriptLoadPromise = null;
      reject(new Error('Failed to load AppsFlyer Smart Script'));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

/**
 * Check if AppsFlyer Smart Script is loaded and ready
 */
export const isSmartScriptReady = (): boolean => {
  return !!(isScriptLoaded && window.AF_SMART_SCRIPT?.generateOneLinkURL);
};

/**
 * Generate OneLink URL using AppsFlyer Smart Script
 * This uses the configuration from constants and dynamically binds values
 *
 * @param additionalParams - Additional parameters to include in the URL
 * @returns IOneLinkResult with clickURL (or null if generation fails)
 */
export const generateOneLinkURL = (
  additionalParams?: Record<string, string>
): IOneLinkResult => {
  // Check if Smart Script is available
  if (!window.AF_SMART_SCRIPT?.generateOneLinkURL) {
    log({
      errTitle: '[AppMigration] AppsFlyer Smart Script not loaded. Falling back to direct URL.',
      tags: { [TAG_IDENTIFIER.MODULE]: 'AppsFlyerSmartScript' },
    });
    return { clickURL: null };
  }

  try {
    // Build the configuration object matching AppsFlyer's expected format
    const config: ISmartScriptConfig = {
      oneLinkURL: APPSFLYER_CONFIG.oneLinkURL,
      afParameters: {
        mediaSource: APPSFLYER_CONFIG.mediaSource,
        afCustom: [
          ...APPSFLYER_CONFIG.customParams,
          // Add any additional custom params
          ...(additionalParams
            ? Object.entries(additionalParams).map(([key, value]) => ({
                paramKey: key,
                defaultValue: value,
              }))
            : []),
        ],
      },
    };

    // Generate the OneLink URL
    const result = window.AF_SMART_SCRIPT.generateOneLinkURL(config);

    return result;
  } catch (error) {
    log({
      errTitle: '[AppMigration] Error generating OneLink URL',
      error,
      tags: { [TAG_IDENTIFIER.MODULE]: 'AppsFlyerSmartScript' },
    });
    return { clickURL: null };
  }
};

/**
 * Sanitize a URL to prevent XSS by ensuring it uses a safe protocol
 * Only allows http and https protocols
 *
 * @param url - The URL to sanitize
 * @returns string - The sanitized URL or empty string if unsafe
 */
const sanitizeURL = (url: string): string => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
    return '';
  } catch {
    return '';
  }
};

/**
 * Generate a fallback OneLink URL without the Smart Script
 * This is used when the Smart Script fails to load or isn't available
 *
 * @param additionalParams - Additional parameters to include
 * @returns string - The generated OneLink URL
 */
export const generateFallbackOneLinkURL = (
  additionalParams?: Record<string, string>
): string => {
  const currentUrl =
    typeof window !== 'undefined' ? sanitizeURL(window.location.href) : '';

  const params = new URLSearchParams({
    pid: 'mweb',
    c: 'mweb_to_app',
    af_ss_ui: 'true',
    deep_link_value: currentUrl,
    af_dp: currentUrl,
    af_web_dp: currentUrl,
    ...additionalParams,
  });

  return `${APPSFLYER_CONFIG.oneLinkURL}?${params.toString()}`;
};

/**
 * Initialize AppsFlyer Smart Script and generate OneLink URL
 * This is the main function to use for getting the redirect URL
 *
 * @param additionalParams - Additional parameters for tracking
 * @returns Promise<string> - The OneLink URL to redirect to
 */
export const initAndGenerateOneLinkURL = async (
  additionalParams?: Record<string, string>
): Promise<string> => {
  try {
    // Load the Smart Script
    await loadAppsFlyerSmartScript();

    // Generate the OneLink URL
    const result = generateOneLinkURL(additionalParams);

    if (result.clickURL) {
      return result.clickURL;
    }

    // Fallback if Smart Script returns null
    return generateFallbackOneLinkURL(additionalParams);
  } catch (error) {
    log({
      errTitle: '[AppMigration] Smart Script failed, using fallback',
      error,
      tags: { [TAG_IDENTIFIER.MODULE]: 'AppsFlyerSmartScript' },
    });
    return generateFallbackOneLinkURL(additionalParams);
  }
};
